// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";

// Models
import { PersonModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PersonModel";

// Dto
import { PropertyDetailDTO } from "Globals/Models/DTO/PropertyDetailDTO";
import { PropertyDetailPersonDTO } from "Globals/Models/DTO/PropertyDetailPersonDTO";

export class PropertyDetailsModel extends ModelBase<PropertyDetailsModel, PropertyDetailsAndRelated> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string = "";
    @MobX.observable public applicantId: string = "";
    @MobX.observable public isCouncilTenant: boolean = false;
    @MobX.observable public isHousingAssociationTenant: boolean = false;
    @MobX.observable public isRentingProperty: boolean = false;
    @MobX.observable public isRentingFromEmployer: boolean = false;
    @MobX.observable public isLivingWithFamily: boolean = false;
    @MobX.observable public isInTempAccommodation: boolean = false;
    @MobX.observable public isHomeOwner: boolean = false;
    @MobX.observable public landlordName: string = "";
    @MobX.observable public landlordPhone: string = "";
    @MobX.observable public landlordAddressLine1: string = "";
    @MobX.observable public landlordAddressLine2: string = "";
    @MobX.observable public landlordAddressLine3: string = "";
    @MobX.observable public landlordCity: string = "";
    @MobX.observable public landlordCounty: string = "";
    @MobX.observable public landlordPostcode: string = "";
    @MobX.observable public isRegisteredWithHelpToBuy: boolean = false;
    @MobX.observable public helpToBuyRegistrationNumber: string = "";
    @MobX.observable public hasPets: boolean = true;
    @MobX.observable public petsNotes: string = "";
    @MobX.observable public isAnyoneLivingWithYou: boolean = true;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @MobX.observable public personModels: PersonModel[] = [];

    @MobX.action
    public fromDto = (model: PropertyDetailsAndRelated): void => {
        if (model !== null && model !== undefined) {
            this.id = model.propertyDetail.id;
            this.formId = model.propertyDetail.formId;
            this.applicantId = model.propertyDetail.applicantId;
            this.isCouncilTenant = model.propertyDetail.isCouncilTenant;
            this.isHousingAssociationTenant = model.propertyDetail.isHousingAssociationTenant;
            this.isRentingProperty = model.propertyDetail.isRentingProperty;
            this.isRentingFromEmployer = model.propertyDetail.isRentingFromEmployer;
            this.isLivingWithFamily = model.propertyDetail.isLivingWithFamily;
            this.isInTempAccommodation = model.propertyDetail.isInTempAccommodation;
            this.isHomeOwner = model.propertyDetail.isHomeOwner;
            this.landlordName = model.propertyDetail.landlordName;
            this.landlordPhone = model.propertyDetail.landlordPhone;
            this.landlordAddressLine1 = model.propertyDetail.landlordAddressLine1;
            this.landlordAddressLine2 = model.propertyDetail.landlordAddressLine2;
            this.landlordAddressLine3 = model.propertyDetail.landlordAddressLine3;
            this.landlordCity = model.propertyDetail.landlordCity;
            this.landlordCounty = model.propertyDetail.landlordCounty;
            this.landlordPostcode = model.propertyDetail.landlordPostcode;
            this.isRegisteredWithHelpToBuy = model.propertyDetail.isRegisteredWithHelpToBuy;
            this.helpToBuyRegistrationNumber = model.propertyDetail.helpToBuyRegistrationNumber;
            this.hasPets = model.propertyDetail.hasPets;
            this.petsNotes = model.propertyDetail.petsNotes;
            this.isAnyoneLivingWithYou = model.propertyDetail.isAnyoneLivingWithYou;
            this.createdDate = model.propertyDetail.createdDate;
            this.isDeleted = model.propertyDetail.isDeleted;

            // Who else you live with
            if (model.personModels) {
                model.personModels.forEach((person) => {
                    let localPersonModel: PersonModel = new PersonModel();
                    localPersonModel.fromDto(person);
                    this.personModels.push(localPersonModel);
                });
            }
        }
    };

    @MobX.action
    public fromPropertyDetailDto = (model: PropertyDetailDTO): void => {
        if (model !== null && model !== undefined) {
            this.id = model.id;
            this.formId = model.formId;
            this.applicantId = model.applicantId;
            this.isCouncilTenant = model.isCouncilTenant;
            this.isHousingAssociationTenant = model.isHousingAssociationTenant;
            this.isRentingProperty = model.isRentingProperty;
            this.isRentingFromEmployer = model.isRentingFromEmployer;
            this.isLivingWithFamily = model.isLivingWithFamily;
            this.isInTempAccommodation = model.isInTempAccommodation;
            this.isHomeOwner = model.isHomeOwner;
            this.landlordName = model.landlordName;
            this.landlordPhone = model.landlordPhone;
            this.landlordAddressLine1 = model.landlordAddressLine1;
            this.landlordAddressLine2 = model.landlordAddressLine2;
            this.landlordAddressLine3 = model.landlordAddressLine3;
            this.landlordCity = model.landlordCity;
            this.landlordCounty = model.landlordCounty;
            this.landlordPostcode = model.landlordPostcode;
            this.isRegisteredWithHelpToBuy = model.isRegisteredWithHelpToBuy;
            this.helpToBuyRegistrationNumber = model.helpToBuyRegistrationNumber;
            this.hasPets = model.hasPets;
            this.petsNotes = model.petsNotes;
            this.isAnyoneLivingWithYou = model.isAnyoneLivingWithYou;
            this.createdDate = model.createdDate;
            this.isDeleted = model.isDeleted;
        }
    };

    @MobX.action
    public toDto = (): PropertyDetailDTO => {
        let model: PropertyDetailDTO = {
            id: this.id,
            formId: this.formId,
            applicantId: this.applicantId,
            isCouncilTenant: this.isCouncilTenant,
            isHousingAssociationTenant: this.isHousingAssociationTenant,
            isRentingProperty: this.isRentingProperty,
            isRentingFromEmployer: this.isRentingFromEmployer,
            isLivingWithFamily: this.isLivingWithFamily,
            isInTempAccommodation: this.isInTempAccommodation,
            isHomeOwner: this.isHomeOwner,
            landlordName: this.landlordName,
            landlordPhone: this.landlordPhone,
            landlordAddressLine1: this.landlordAddressLine1,
            landlordAddressLine2: this.landlordAddressLine2,
            landlordAddressLine3: this.landlordAddressLine3,
            landlordCity: this.landlordCity,
            landlordCounty: this.landlordCounty,
            landlordPostcode: this.landlordPostcode,
            isRegisteredWithHelpToBuy: this.isRegisteredWithHelpToBuy,
            helpToBuyRegistrationNumber: this.helpToBuyRegistrationNumber,
            hasPets: this.hasPets,
            petsNotes: this.petsNotes,
            isAnyoneLivingWithYou: this.isAnyoneLivingWithYou,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };
}

// Extend interface to accept new form sections
export interface PropertyDetailsAndRelated {
    propertyDetail: PropertyDetailDTO;
    personModels: PropertyDetailPersonDTO[] | undefined;
}
